import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, ArrowLeftRight, Wallet, Settings, LogOut, Menu, X, UsersRound, MapPin, ClipboardList, Bike } from "lucide-react";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const nav = [
    { to: "/admin", end: true, label: t("dashboard"), icon: LayoutDashboard, id: "nav-dashboard" },
    { to: "/admin/agents", label: t("agents"), icon: Users, id: "nav-agents" },
    { to: "/admin/mesajes", label: "Mesajé", icon: Bike, id: "nav-mesajes" },
    { to: "/admin/team", label: "Team", icon: UsersRound, id: "nav-team" },
    { to: "/admin/zones", label: "Zon / Adrès", icon: MapPin, id: "nav-zones" },
    { to: "/admin/transfers", label: t("transfers"), icon: ArrowLeftRight, id: "nav-transfers" },
    { to: "/admin/withdrawals", label: t("withdrawals"), icon: Wallet, id: "nav-withdrawals" },
    { to: "/admin/kontwol", label: "Tablo Kontwòl", icon: ClipboardList, id: "nav-kontwol" },
    { to: "/admin/settings", label: t("settings"), icon: Settings, id: "nav-settings" },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const NavItems = () => (
    <>
      {nav.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          data-testid={n.id}
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `group flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors ${
              isActive
                ? "bg-brand-blue text-white shadow-md shadow-brand-blue/25"
                : "text-muted-foreground hover:bg-secondary hover:text-foreground"
            }`
          }
        >
          <n.icon className="h-4 w-4" />
          {n.label}
        </NavLink>
      ))}
    </>
  );

  return (
    <div className="min-h-dvh w-full max-w-[100vw] overflow-x-hidden bg-background flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-border bg-card/50">
        <div className="p-5 border-b border-border">
          <Logo />
        </div>
        <nav className="flex-1 p-3 space-y-1">
          <NavItems />
        </nav>
        <div className="p-3 border-t border-border">
          <div className="mb-2 px-2 text-xs text-muted-foreground truncate">{user?.email}</div>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start gap-2"
            onClick={handleLogout}
            data-testid="admin-logout-btn"
          >
            <LogOut className="h-4 w-4" /> {t("logout")}
          </Button>
        </div>
      </aside>

      {/* Mobile sidebar overlay */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-[min(18rem,88vw)] bg-card border-r border-border flex flex-col safe-area-pad">
            <div className="p-4 flex items-center justify-between border-b border-border">
              <Logo />
              <Button variant="ghost" size="icon" onClick={() => setOpen(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
              <NavItems />
            </nav>
            <div className="p-3 border-t border-border">
              <div className="mb-2 px-2 text-xs text-muted-foreground truncate">{user?.email}</div>
              <Button variant="outline" size="sm" className="w-full gap-2" onClick={handleLogout}>
                <LogOut className="h-4 w-4" /> {t("logout")}
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Main area — full remaining width on mobile */}
      <div className="flex-1 flex flex-col min-w-0 w-full">
        <header className="sticky top-0 z-40 h-14 sm:h-16 border-b border-border bg-background/80 backdrop-blur-md flex items-center gap-2 sm:gap-3 px-3 sm:px-4 lg:px-8 w-full">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden shrink-0"
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="lg:hidden shrink-0"><Logo size={28} showText={false} /></div>
          <div className="flex-1 min-w-0" />
          <LanguageSwitcher />
          <ThemeToggle />
        </header>
        <main className="flex-1 w-full min-w-0 px-3 py-4 sm:px-6 sm:py-6 lg:p-10 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <div className="w-full max-w-full">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
